import { useState } from "react"
import { ProductCategories } from "@/components/Navbar/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Slider } from "@/components/ui/slider"
import { PreviewOverlay } from "components/PreviewOverlay"
import { TintViewer } from "components/TintViewer/TintViewer"
import { Expand } from "lucide-react"
import { type RuntimeGlassesControls } from "services/player/glasses-effect"
import { initRenderingOptions, type ProductV2 } from "types"

const formatValue = (value: number) => value.toFixed(2).replace(/\.00$/, "")

const SliderRow = ({
  disabled,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: {
  disabled?: boolean
  label: string
  max: number
  min: number
  onChange: (value: number) => void
  step: number
  value: number
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium">{label}</span>
      <div className="rounded-md border px-2 py-1 text-xs text-muted-foreground">
        {formatValue(value)}
      </div>
    </div>
    <Slider
      data-testid={`glasses-${label.toLowerCase().replaceAll(" ", "-")}-slider`}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      value={[value]}
      onValueChange={([nextValue]) => onChange(nextValue)}
    />
  </div>
)

const ExpandButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="outline"
    size="icon"
    className="absolute right-3 top-3 z-10 size-8 bg-background/95"
    onClick={onClick}
    data-testid="glasses-preview-expand"
  >
    <Expand className="size-4" />
  </Button>
)

const TryOnFrame = ({
  glassesTransformMode,
  onGlassesTransformsResolved,
  onExpand,
  previewImage,
  previewProduct,
  show,
}: {
  glassesTransformMode: "raw" | "controlled"
  onGlassesTransformsResolved?: (value: RuntimeGlassesControls | null) => void
  onExpand: () => void
  previewImage: string
  previewProduct: ProductV2
  show: boolean
}) => (
  <div
    className="relative aspect-square w-full overflow-hidden rounded-xl"
    data-testid="glasses-try-on-frame"
  >
    <ExpandButton onClick={onExpand} />
    {show ? (
      <TintViewer
        effectType={ProductCategories.GLASSES}
        glassesTransformMode={glassesTransformMode}
        image={previewImage}
        onGlassesTransformsResolved={onGlassesTransformsResolved}
        productItem={previewProduct}
      />
    ) : null}
  </div>
)

export const GlassesPreviewCard = ({
  glassesTransformMode,
  onGlassesTransformsResolved,
  isParsingTryOn,
  isReady,
  onPositionXChange,
  onPreviewImageChange,
  onScaleChange,
  onPositionYChange,
  previewProduct,
  previewImage,
}: {
  glassesTransformMode: "raw" | "controlled"
  onGlassesTransformsResolved: (value: RuntimeGlassesControls | null) => void
  isParsingTryOn: boolean
  isReady: boolean
  onPositionXChange: (value: number) => void
  onPreviewImageChange: (value: string) => void
  onScaleChange: (value: number) => void
  onPositionYChange: (value: number) => void
  previewProduct: ProductV2
  previewImage: string
}) => {
  const [previewOverlayOpen, setPreviewOverlayOpen] = useState(false)
  const renderingOptions = previewProduct.renderingOptions ?? initRenderingOptions
  const showStaticImagePreview = !isReady

  const openOverlay = () => setPreviewOverlayOpen(true)

  const overlay = previewOverlayOpen ? (
    <PreviewOverlay
      effectType={ProductCategories.GLASSES}
      glassesTransformMode={glassesTransformMode}
      image={previewImage}
      onChangeImage={onPreviewImageChange}
      onClose={() => setPreviewOverlayOpen(false)}
      productItem={previewProduct}
    />
  ) : null

  if (showStaticImagePreview)
    return (
      <>
        {overlay}
        {previewImage ? (
          <Card
            className="mx-auto w-full max-w-[58vh] self-start overflow-hidden"
            data-testid="glasses-preview-static"
          >
            <CardContent className="p-4">
              <div className="relative aspect-square w-full overflow-hidden rounded-xl">
                <ExpandButton onClick={openOverlay} />
                <img
                  src={previewImage}
                  alt="Preview"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            className="mx-auto flex aspect-square w-full max-w-[58vh] self-start items-center justify-center rounded-xl border border-dashed bg-muted/20 px-6 text-center"
            data-testid="glasses-preview-placeholder"
          >
            <p className="text-sm text-muted-foreground">Try-on preview will appear here</p>
          </div>
        )}
      </>
    )

  return (
    <Card
      className="mx-auto w-full max-w-[58vh] self-start overflow-hidden"
      data-testid="glasses-preview-card"
    >
      {overlay}
      <CardContent className="flex flex-col gap-5 p-4">
        <TryOnFrame
          glassesTransformMode={glassesTransformMode}
          onGlassesTransformsResolved={onGlassesTransformsResolved}
          onExpand={openOverlay}
          previewImage={previewImage}
          previewProduct={previewProduct}
          show={!previewOverlayOpen}
        />
        {isParsingTryOn ? (
          <div
            className="rounded-lg border border-dashed bg-muted/20 px-4 py-3 text-sm text-muted-foreground"
            data-testid="glasses-preview-syncing"
          >
            Syncing current effect values...
          </div>
        ) : null}
        <SliderRow
          disabled={isParsingTryOn}
          label="Scale"
          min={-2.5}
          max={2.5}
          step={0.01}
          value={renderingOptions.scale.x}
          onChange={onScaleChange}
        />
        <SliderRow
          disabled={isParsingTryOn}
          label="Position X"
          min={-100}
          max={100}
          step={0.1}
          value={renderingOptions.positionAccuracy.x}
          onChange={onPositionXChange}
        />
        <SliderRow
          disabled={isParsingTryOn}
          label="Position Y"
          min={-100}
          max={100}
          step={0.1}
          value={renderingOptions.positionAccuracy.y}
          onChange={onPositionYChange}
        />
      </CardContent>
    </Card>
  )
}
